import { SessionStartRequest, SessionStartResponse } from '../types';

export class ARCOSSessionService {
    private baseUrl: string;
    private apiKey: string;
    private isProdLike: boolean;

    constructor() {
        this.baseUrl =
            (import.meta as ImportMeta & { env?: Record<string, string> }).env
                ?.VITE_ARCOS_API_URL || 'http://localhost:8000';
        this.apiKey =
            (import.meta as ImportMeta & { env?: Record<string, string> }).env
                ?.VITE_ARCOS_API_KEY || 'dev-key';
        const appEnv = (import.meta as ImportMeta & { env?: Record<string, string> }).env
            ?.VITE_APP_ENV;
        this.isProdLike =
            import.meta.env.MODE === 'production' ||
            appEnv === 'production' ||
            appEnv === 'staging';
    }

    async startSession(request: SessionStartRequest): Promise<SessionStartResponse> {
        try {
            const response = await fetch(`${this.baseUrl}/api/arcos/startSession`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${this.apiKey}`,
                },
                body: JSON.stringify({
                    sessionId: request.sessionId,
                    controlMode: request.controlMode,
                    pluginMemory: request.pluginMemory,
                    promptKit: request.promptKit,
                }),
            });

            if (!response.ok) {
                const bodyText = await response.text().catch(() => '');
                throw new Error(
                    `ARCOS API error: ${response.status}${bodyText ? ` ${bodyText}` : ''}`
                );
            }

            const data = await response.json();
            return {
                sessionId: data.sessionId || request.sessionId,
                status: 'success',
                message: data.message,
            };
        } catch (error) {
            console.error('ARCOS session start error:', error);

            // No silent success in prod/staging.
            if (this.isProdLike) {
                return {
                    sessionId: request.sessionId,
                    status: 'error',
                    message: error instanceof Error ? error.message : 'ARCOS session start failed',
                };
            }

            // Dev fallback (explicit): allow local UI to proceed without ARCOS backend.
            return {
                sessionId: request.sessionId,
                status: 'success',
                message: 'Session initialized with dev fallback (ARCOS unavailable)',
            };
        }
    }

    async setBeeControlMode(sessionId: string, mode: 'assist' | 'autonomous'): Promise<boolean> {
        try {
            const response = await fetch(`${this.baseUrl}/api/arcos/session/${sessionId}/control`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${this.apiKey}`,
                },
                body: JSON.stringify({ controlMode: mode }),
            });

            return response.ok;
        } catch (error) {
            console.error('Error setting control mode:', error);
            return !this.isProdLike;
        }
    }

    async populatePluginMemory(sessionId: string, plugins: string[]): Promise<boolean> {
        try {
            const response = await fetch(`${this.baseUrl}/api/arcos/session/${sessionId}/plugins`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${this.apiKey}`,
                },
                body: JSON.stringify({ pluginMemory: plugins }),
            });

            return response.ok;
        } catch (error) {
            console.error('Error populating plugin memory:', error);
            return !this.isProdLike;
        }
    }
}

export const arcosSessionService = new ARCOSSessionService();
